import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  User,
  LoginRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  AuthResult,
  getCurrentUser,
  login as authLogin,
  logout as authLogout,
  forgotPassword as authForgotPassword,
  resetPassword as authResetPassword,
  validateToken,
  isAuthenticated as checkIsAuthenticated,
  isTokenExpiringSoon,
  removeAuthToken,
  removeCurrentUser
} from '../services/authService';
import {
  Capabilities,
  Capability,
  Role,
  readCapabilities,
  hasCapability
} from '../utils/capabilities';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<AuthResult>;
  logout: () => Promise<void>;
  forgotPassword: (request: ForgotPasswordRequest) => Promise<AuthResult>;
  resetPassword: (request: ResetPasswordRequest) => Promise<AuthResult>;
  // --- מודל היכולות (מקור-אמת יחיד, נגזר מה-JWT) ---
  role: Role | null;
  isManager: boolean;
  isEmployee: boolean;
  isAgent: boolean;
  isStaff: boolean;
  canViewClientFinance: boolean;
  canViewFinancialReports: boolean;
  canManageSettings: boolean;
  departmentIds: number[];
  can: (capability: Capability) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [capabilities, setCapabilities] = useState<Capabilities | null>(null);

  // Initialize auth state on app load
  useEffect(() => {
    const initializeAuth = async () => {
      setIsLoading(true);

      try {
        // Check if user is stored in localStorage and token is valid
        const storedUser = getCurrentUser();
        const isAuth = checkIsAuthenticated();

        if (storedUser && isAuth) {
          // Validate token with server
          const isValidToken = await validateToken();

          if (isValidToken) {
            // מודל היכולות מגיע אך ורק מה-claims החדשים ב-JWT.
            const caps = readCapabilities();

            if (!caps) {
              // טוקן ישן ללא ה-claims החדשים (הונפק לפני P2b) — כפיית re-login נקי
              // במקום רינדור שגוי. ProtectedRoute ינתב ל-/login.
              removeAuthToken();
              removeCurrentUser();
              setUser(null);
              setCapabilities(null);
              setIsAuthenticated(false);
            } else {
              setUser(storedUser);
              setCapabilities(caps);
              setIsAuthenticated(true);
            }
          } else {
            // Token is invalid, clear auth state
            setUser(null);
            setCapabilities(null);
            setIsAuthenticated(false);
          }
        } else {
          setUser(null);
          setCapabilities(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        setUser(null);
        setCapabilities(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Check for token expiration periodically
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkTokenExpiration = () => {
      if (isTokenExpiringSoon()) {
        // Show notification that session will expire soon
        console.warn('Session will expire soon');
        // In a real app, you might show a modal asking if user wants to extend session
      }
    };

    // Check every minute
    const interval = setInterval(checkTokenExpiration, 60000);

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const login = async (credentials: LoginRequest): Promise<AuthResult> => {
    setIsLoading(true);

    try {
      const result = await authLogin(credentials);

      if (result.isSuccess && result.data) {
        const userData = result.data.user;
        // הטוקן הטרי (post-P2b) נושא את ה-claims החדשים → caps תמיד קיימות כאן.
        setUser(userData);
        setCapabilities(readCapabilities());
        setIsAuthenticated(true);
      }

      return result;
    } catch (error) {
      console.error('Login error:', error);
      return {
        isSuccess: false,
        message: 'שגיאה בהתחברות'
      };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    setIsLoading(true);

    try {
      await authLogout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setCapabilities(null);
      setIsAuthenticated(false);
      setIsLoading(false);
    }
  };

  const forgotPassword = async (request: ForgotPasswordRequest): Promise<AuthResult> => {
    return await authForgotPassword(request);
  };

  const resetPassword = async (request: ResetPasswordRequest): Promise<AuthResult> => {
    return await authResetPassword(request);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    forgotPassword,
    resetPassword,
    role: capabilities?.role ?? null,
    isManager: capabilities?.isManager ?? false,
    isEmployee: capabilities?.isEmployee ?? false,
    isAgent: capabilities?.isAgent ?? false,
    isStaff: capabilities?.isStaff ?? false,
    canViewClientFinance: capabilities?.canViewClientFinance ?? false,
    canViewFinancialReports: capabilities?.canViewFinancialReports ?? false,
    canManageSettings: capabilities?.canManageSettings ?? false,
    departmentIds: capabilities?.departmentIds ?? [],
    can: (capability: Capability) => hasCapability(capabilities, capability)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
